import react, { useState, useEffect, use } from "react";
import PreviousConversationsList from "./PreviousConversationsList";
import GuideDashboardHeader from "./GuideDashboardHeader";
import Footer from "./Footer";

const ViewConversations = (props) => {

    const [notes, setNotes] = useState([]);
    const [showNoteDetails, setShowNoteDetails] = useState(false);
    const [showConversationDetails, setShowConversationDetails] = useState(false);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [selectedNote, setSelectedNote] = useState(null);
    const [craftingReply, setCraftingReply] = useState(false);
    const [replyMessage, setReplyMessage] = useState("");
    const [useVideoReply, setUseVideoReply] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [noteNeedsReply, setNoteNeedsReply] = useState(true);
    const [editTextNote, setEditTextNote] = useState(false);
    const [editVideoNote, setEditVideoNote] = useState(false);


    // Fetch notes data from API
    const fetchConversations = async () => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/guide/conversations`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${window.localStorage.getItem('guideToken')}`
            }
        });
        const data = await response.json();
        setSelectedConversation(null);
        if(data.expiredToken) {
            props.showTheExpiredTokenModal();
            return;
        }
        if (data.success) {
            setConversations(data.conversations);
        } else {
            console.error("Failed to fetch conversations");
        }
    };

    const fetchNotesInThisConversation = async (guideReplyNoteId) => {
        if(!guideReplyNoteId) return false;
        const response = await fetch(`${process.env.REACT_APP_API_URL}/guide/conversation/notes/${guideReplyNoteId}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${window.localStorage.getItem('guideToken')}`
            }
        });
        const data = await response.json();
        if (data.success) {
            setNotes(data.notes);
            const needsReply = data.notes.some(note => note.sentBy !== "guide" && !note.guideReplied);
            setNoteNeedsReply(needsReply);
        } else {
            console.error("Failed to fetch notes");
        }
        return true;
    };

     const showTheConversationDetails = async (conversation) => {
        setSelectedConversation(conversation);
        setShowConversationDetails(true);
        await fetchNotesInThisConversation(conversation._id);
    }

    useEffect(() => {
        if(selectedConversation) {
            fetchNotesInThisConversation(selectedConversation._id);
        } else {
            console.log("Fetching conversations because no conversation is selected");
            fetchConversations();
        }
    }, []);

    const handleClickReplyToConvo = (note) => {
        setCraftingReply(true);
        setSelectedNote(note);
        if(note.type === "video") {
            setUseVideoReply(true);
        } else {
            setUseVideoReply(false);
        }
    }

    const uploadVideoDirectly = async (file, noteID) => {
        setUploadingVideo(true);
        try {
            // 1. Get pre-signed URL from your backend
            const response = await fetch(process.env.REACT_APP_API_URL+'/guide/generate-video-upload-url', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${window.localStorage.getItem('guideToken')}`
                },
                body: JSON.stringify({
                    fileName: file.name,
                    fileType: file.type,
                    noteID: noteID
                })
            });

            const { uploadUrl, videoKey } = await response.json();

            // 2. Upload directly to S3
            const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type
                }
            });

            if (uploadResponse.ok) {
                // 3. Notify your backend that upload is complete
                const completedVideoUpload = await fetch(process.env.REACT_APP_API_URL+'/guide/video-upload-complete', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${window.localStorage.getItem('guideToken')}`
                    },
                    body: JSON.stringify({
                        videoKey: videoKey,
                        noteID: noteID,
                        editingGuideNote: editVideoNote
                    })
                });

                console.log('Video uploaded successfully!');
                await fetchNotesInThisConversation(selectedConversation._id);
                setUploadingVideo(false);
                setCraftingReply(false);
                setEditVideoNote(false);

            }
        } catch (error) {
            console.error('Upload failed:', error);
        }
    };

    const deployVideoReponse = (noteID) => {
        fetch(`${process.env.REACT_APP_API_URL}/guide/deploy-video-reply`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                "Authorization": `Bearer ${window.localStorage.getItem('guideToken')}`
            },
            body: JSON.stringify({
                noteID: noteID
            }),
        })
        .then(response => response.json())
        .then(async data => {
            if (data.success) {
                setCraftingReply(true);
                await fetchNotesInThisConversation(selectedConversation._id);
                setEditVideoNote(false);
            } else {
                console.error("Failed to deploy video reply");
                alert("Failed to deploy video reply. Please try again.");
            }
        })
        .catch(error => {
            console.error("Error occurred during deploying video reply:", error);
            alert("An error occurred. Please try again later.");
        });
    }

    const deleteVideoReponse = (noteID) => {
        fetch(`${process.env.REACT_APP_API_URL}/guide/delete-video-reply`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                "Authorization": `Bearer ${window.localStorage.getItem('guideToken')}`
            },
            body: JSON.stringify({
                noteID: noteID
            }),
        })
        .then(response => response.json())
        .then(async data => {
            if (data.success) {
                alert("Video reply deleted successfully!");
                setCraftingReply(true);
                await fetchNotesInThisConversation(selectedConversation._id);
            } else {
                console.error("Failed to delete video reply");
                alert("Failed to delete video reply. Please try again.");
            }
        })
        .catch(error => {
            console.error("Error occurred during deleting video reply:", error);
            alert("An error occurred. Please try again later.");
        });
    }

    const handleSendReply = () => {
        if(!replyMessage || replyMessage.trim() === "") {
            alert("Reply message cannot be empty.");
            return;
        }
        fetch(`${process.env.REACT_APP_API_URL}/guide/send-text-reply`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                "Authorization": `Bearer ${window.localStorage.getItem('guideToken')}`
            },
            body: JSON.stringify({
                noteID: selectedNote._id,
                message: replyMessage
            }),
        })
        .then(response => response.json())
        .then(async data => {
            if (data.success) {
                setCraftingReply(false);
                setReplyMessage("");
                await fetchNotesInThisConversation(selectedConversation._id);
            } else {
                console.error("Failed to send reply");
                alert("Failed to send reply. Please try again.");
            }
        })
        .catch(error => {
            console.error("Error occurred during sending reply:", error);
            alert("An error occurred. Please try again later.");
        });
    }

    const handleEditTextNote = (note) => {
        setSelectedNote(note);
        setReplyMessage(note.message);
        setEditTextNote(true);
    }

    const handleEditVideoNote = (note) => {
        setSelectedNote(note);
        setEditVideoNote(true);
    }

    const saveEditedTextNote = () => {
        setEditTextNote(false);
        fetch(`${process.env.REACT_APP_API_URL}/guide/edit-text-reply`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                "Authorization": `Bearer ${window.localStorage.getItem('guideToken')}`
            },
            body: JSON.stringify({
                noteID: selectedNote._id,
                message: replyMessage
            }),
        })
        .then(response => response.json())
        .then(async data => {
            if (data.success) {
                setReplyMessage("");
                await fetchNotesInThisConversation(selectedConversation._id);
            } else {
                console.error("Failed to save edited text note");
                alert("Failed to save edited text note. Please try again.");
            }
        })
        .catch(error => {
            console.error("Error occurred during saving edited text note:", error);
            alert("An error occurred. Please try again later.");
        });
    }

    return (
        <>
            <GuideDashboardHeader />
            <section style={{ padding: 20, minHeight: '80vh' }}>
            <h2 className="guide-title">Conversations</h2>
            {props.isLoggedIn && (
                <div className="letter-card-container">
                    {!selectedConversation && conversations.length > 0 && <PreviousConversationsList conversations={conversations} showTheConversationDetails={showTheConversationDetails} />}
                    {!showConversationDetails && conversations.length === 0 && (
                        <div style={{ textAlign: "center", marginTop: 20 }}>
                            <h3>No conversations found.</h3>
                            <p>Please check back later.</p>
                        </div>
                    )}
                    {selectedConversation && <h2 className="guide-title"><span style={{color: "#fe9628", cursor: "pointer"}} onClick={() => { setCraftingReply(false); setSelectedConversation(null); }}>Conversations</span> ➡️ {selectedConversation.name}</h2>}
                    {selectedConversation && notes.length > 0 && notes.map((note) => {
                        if(note.sentBy !== "guide") {
                            let earnAmount = 0;
                            if(note.price) {
                                let priceNumber = parseFloat(note.price.replace("$",""));
                                earnAmount = "$"+(priceNumber - (priceNumber * 0.20)).toFixed(2); // Assuming 20% fee
                            }
                            return (
                                <div className="letter-card" key={note._id}>
                                    <div className="stamp">💌</div>

                                    <div className="letter-content">
                                        <div className="note-header-with-img">
                                            <img 
                                                height="auto" 
                                                className="note-profile-picture" 
                                                src={note.profilePictureUrl} 
                                                alt="Profile" 
                                                loading="lazy"
                                            />
                                            <h3><span>{note.customerName}</span> wrote:</h3>
                                        </div>
                                        <div style={{ whiteSpace: "pre-wrap", maxHeight: "300px", overflowY: "scroll" }}>{note.message}</div>
                                        {!note.guideReplied && (
                                            <div>
                                                <p className="reward">{!craftingReply && <span className="type-of-reply-required">Earn {earnAmount} by sending a {note.type} reply to {note.customerName.split(" ")[0]}</span>}</p>
                                                {!craftingReply && <button className="reply-btn" onClick={() => handleClickReplyToConvo(note)}>Reply</button>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        } else {
                            return (
                                <div className="letter-card guide-reply" key={note._id}>
                                    {!note.readByUser && <div className="stamp">
                                        {note.type === "text" && <span className="note-read-status">Unread 📫</span>}
                                        {note.type === "video" && <span className="note-read-status">Unseen 📫</span>}
                                        {note.type === "text" && !editTextNote && <span onClick={() => handleEditTextNote(note)} className="edit-note-button">edit</span>}
                                        {note.type === "text" && editTextNote && <span onClick={saveEditedTextNote} className="edit-note-button">save</span>}
                                        {note.type === "video" && !editVideoNote && <span onClick={() => handleEditVideoNote(note)} className="edit-note-button">edit</span>}
                                        {note.type === "video" && editVideoNote && <span onClick={() => setEditVideoNote(false)} className="edit-note-button">cancel</span>}
                                    </div>}
                                    {note.readByUser && <div className="stamp">
                                        {note.type === "text" && <span className="note-read-status">Read 📬</span>}
                                        {note.type === "video" && <span className="note-read-status">Seen 📬</span>}
                                    </div>}
                                    <div className="letter-content">
                                        <div className="note-header-with-img">
                                            <img 
                                                height="auto" 
                                                className="note-profile-picture" 
                                                src={note.profilePictureUrl} 
                                                alt="Profile" 
                                                loading="lazy"
                                            />
                                            <h3><span>{note.guideName} </span><br /> replied to <span>{note.customerName.split(" ")[0]}</span></h3>
                                        </div>
                                        {note.type === "video" && !editVideoNote && (
                                            <div>
                                                <video 
                                                    className="video-note" 
                                                    controls
                                                    onError={(e) => {
                                                        console.error('Video failed to load:', note.videoUrl);
                                                        console.error('Error details:', e.target.error);
                                                    }}
                                                    onLoadStart={() => console.log('Video loading started:', note.videoUrl)}
                                                    onCanPlay={() => console.log('Video can play:', note.videoUrl)}
                                                >
                                                    <source src={note.videoUrl} type="video/mp4" />
                                                    <source src={note.videoUrl} type="video/webm" />
                                                    <source src={note.videoUrl} type="video/ogg" />
                                                    <source src={note.videoUrl} type="video/mov" />
                                                    Your browser does not support the video tag.
                                                    <p>Video URL: {note.videoUrl}</p>
                                                </video>
                                                {!note.deployed && (
                                                    <div>
                                                        <button className="confirm-video-button" onClick={() => deployVideoReponse(note._id)}>Deploy Response</button><br />
                                                        <button className="cancel-video-button" onClick={() => deleteVideoReponse(note._id)}>Delete</button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {note.type === "video" && editVideoNote && (
                                            <label class="video-upload-label" for="videoUpload">
                                                <span><span className={uploadingVideo ? "block spinning" : ""}> 🎬 </span>{uploadingVideo ? <span className="uploading-video">Uploading</span> :  <span> Upload a Video </span>} <span className={uploadingVideo ? "spinning" : ""}> 🎬 </span></span>
                                                <input
                                                    id="videoUpload"
                                                    accept="video/*"
                                                    class="video-upload-input"
                                                    type="file"
                                                    onChange={(e) => uploadVideoDirectly(e.target.files[0], selectedNote._id)}
                                                />
                                            </label>
                                        )}
                                        {note.type === "text" && !editTextNote && (
                                            <div style={{ whiteSpace: "pre-wrap", maxHeight: "300px", overflowY: "scroll" }}>{note.message}</div> 
                                        )}
                                        {note.type === "text" && editTextNote && (
                                            <textarea
                                                value={replyMessage}
                                                onChange={(e) => setReplyMessage(e.target.value)}
                                                style={{ width: "100%", minHeight: "300px" }}
                                            />
                                        )}
                                    </div>
                                </div> 
                            )
                        }
                    })}
                    {selectedNote && craftingReply && useVideoReply && noteNeedsReply && (
                        <>
                            <h3>Reply to {selectedNote.customerName} with a Video</h3>
                            <p>
                                Create a quick but thoughtful video response to {selectedNote.customerName.split(" ")[0]}.
                                You will have the chance to review your video before you submit it.
                            </p>
                            <label class="video-upload-label" for="videoUpload">
                                <span><span className={uploadingVideo ? "block spinning" : ""}> 🎬 </span>{uploadingVideo ? <span className="uploading-video">Uploading</span> :  <span> Upload a Video </span>} <span className={uploadingVideo ? "spinning" : ""}> 🎬 </span></span>
                                <input
                                    id="videoUpload"
                                    accept="video/*"
                                    class="video-upload-input"
                                    type="file"
                                    onChange={(e) => uploadVideoDirectly(e.target.files[0], selectedNote._id)}
                                />
                            </label>
                        </>
                    )}
                    {selectedNote && craftingReply && !useVideoReply && noteNeedsReply && (
                        <div className="reply-to-conversation-form">
                            <h3>Reply to {selectedNote.customerName}</h3>
                            <textarea className="reply-to-note-textarea" name="replyMessage" placeholder="Type your reply here..." onChange={(e) => setReplyMessage(e.target.value)}></textarea><br />
                            <button className="reply-to-conversation-button" onClick={handleSendReply}>Send Reply</button><br />
                            <button className="cancel-reply-button" onClick={() => setCraftingReply(false)}>Cancel</button>
                        </div>
                    )}
                </div>
            )}
            </section>
            <Footer />
        </>
    );
}

export default ViewConversations;